import React, { useState, useEffect, useCallback } from 'react';
import { isMaster } from '@/lib/permissions';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Database, Search, Trash2, Edit, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, X, Save, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DatabaseEditor = ({ user }) => {
  // State
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selection for bulk delete
  const [selectedDocs, setSelectedDocs] = useState([]);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState({ open: false, document: null });
  const [editData, setEditData] = useState('');
  const [editPassword, setEditPassword] = useState('');
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, documentId: null, isBulk: false });
  const [deletePassword, setDeletePassword] = useState('');

  // Fetch collections on mount
  useEffect(() => {
    if (isMaster(user)) {
      fetchCollections();
    }
  }, [user]);

  const fetchCollections = async () => {
    setCollectionsLoading(true);
    try {
      const response = await api.get('/admin/db/collections');
      setCollections(response.data);
    } catch (error) {
      toast.error('Gagal memuat daftar collection');
      console.error(error);
    } finally {
      setCollectionsLoading(false);
    }
  };

  const fetchDocuments = useCallback(async (collectionName, page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (search) params.append('search', search);
      
      const response = await api.get(`/admin/db/collections/${collectionName}?${params}`);
      setDocuments(response.data.documents);
      setTotalPages(response.data.total_pages);
      setTotalDocs(response.data.total);
      setCurrentPage(response.data.page);
      setSelectedDocs([]);
    } catch (error) {
      toast.error('Gagal memuat data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectCollection = (collectionName) => {
    setSelectedCollection(collectionName);
    setSearchQuery('');
    setCurrentPage(1);
    fetchDocuments(collectionName, 1, '');
  };

  const handleSearch = () => {
    if (selectedCollection) {
      setCurrentPage(1);
      fetchDocuments(selectedCollection, 1, searchQuery);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchDocuments(selectedCollection, newPage, searchQuery);
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(doc => doc.id));
    }
  };

  const toggleSelectDoc = (docId) => {
    if (selectedDocs.includes(docId)) {
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } else {
      setSelectedDocs([...selectedDocs, docId]);
    }
  };

  // Edit handlers
  const openEditDialog = (document) => {
    setEditDialog({ open: true, document });
    setEditData(JSON.stringify(document, null, 2));
    setEditPassword('');
  };

  const handleSaveEdit = async () => {
    if (!editPassword) {
      toast.error('Masukkan password untuk konfirmasi');
      return;
    }

    try {
      const parsedData = JSON.parse(editData);
      await api.put(`/admin/db/collections/${selectedCollection}/${editDialog.document.id}`, {
        password: editPassword,
        data: parsedData
      });
      toast.success('Data berhasil diupdate');
      setEditDialog({ open: false, document: null });
      setEditPassword('');
      fetchDocuments(selectedCollection, currentPage, searchQuery);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error instanceof SyntaxError) {
        toast.error('Format JSON tidak valid');
      } else {
        toast.error('Gagal mengupdate data');
      }
    }
  };

  // Delete handlers
  const openDeleteDialog = (documentId = null, isBulk = false) => {
    setDeleteDialog({ open: true, documentId, isBulk });
    setDeletePassword('');
  };

  const handleDelete = async () => {
    if (!deletePassword) {
      toast.error('Masukkan password untuk konfirmasi');
      return;
    }

    try {
      if (deleteDialog.isBulk) {
        await api.post(`/admin/db/collections/${selectedCollection}/bulk-delete`, {
          password: deletePassword,
          document_ids: selectedDocs
        });
        toast.success(`${selectedDocs.length} dokumen berhasil dihapus`);
        setSelectedDocs([]);
      } else {
        await api.delete(`/admin/db/collections/${selectedCollection}/${deleteDialog.documentId}`, {
          data: { password: deletePassword }
        });
        toast.success('Dokumen berhasil dihapus');
      }
      
      setDeleteDialog({ open: false, documentId: null, isBulk: false });
      setDeletePassword('');
      fetchDocuments(selectedCollection, currentPage, searchQuery);
      fetchCollections(); // Update counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data');
    }
  };

  // Get display columns from first document
  const getDisplayColumns = () => {
    if (documents.length === 0) return [];
    const firstDoc = documents[0];
    const priorityKeys = ['id', 'name', 'username', 'account_name', 'branch_name', 'manager_name', 'amount', 'balance', 'created_at'];
    const keys = Object.keys(firstDoc);
    
    // Sort keys with priority ones first
    return keys.sort((a, b) => {
      const aIndex = priorityKeys.indexOf(a);
      const bIndex = priorityKeys.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    }).slice(0, 6); // Limit to 6 columns
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value).substring(0, 50) + '...';
    if (typeof value === 'string' && value.length > 50) return value.substring(0, 50) + '...';
    return String(value);
  };

  // Access check
  if (!isMaster(user)) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertTriangle size={24} />
              <span className="text-lg font-medium">Akses Ditolak</span>
            </div>
            <p className="text-red-500 mt-2">Halaman ini hanya dapat diakses oleh Admin Pusat.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="database-editor-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Database className="text-purple-600" />
            Database Editor
          </h1>
          <p className="text-slate-500 mt-1">Kelola data langsung dari database</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchCollections(); if (selectedCollection) fetchDocuments(selectedCollection, currentPage, searchQuery); }}
          data-testid="refresh-btn"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Collections List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Collections</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {collectionsLoading ? (
              <div className="p-4 text-center text-slate-500">Loading...</div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                {collections.map((col) => (
                  <div
                    key={col.name}
                    onClick={() => handleSelectCollection(col.name)}
                    className={`px-4 py-3 cursor-pointer flex justify-between items-center border-b hover:bg-slate-50 transition-colors ${
                      selectedCollection === col.name ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''
                    }`}
                    data-testid={`collection-${col.name}`}
                  >
                    <span className="font-medium text-slate-700">{col.name}</span>
                    <span className="text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      {col.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents View */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="text-lg">
                {selectedCollection ? (
                  <>
                    {selectedCollection}
                    <span className="text-sm font-normal text-slate-500 ml-2">({totalDocs} dokumen)</span>
                  </>
                ) : (
                  'Pilih Collection'
                )}
              </CardTitle>
              
              {selectedCollection && (
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Input
                      placeholder="Cari..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="pr-10"
                      data-testid="search-input"
                    />
                    <Search 
                      size={18} 
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer"
                      onClick={handleSearch}
                    />
                  </div>
                  {selectedDocs.length > 0 && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => openDeleteDialog(null, true)}
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 size={16} className="mr-1" />
                      Hapus ({selectedDocs.length})
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          
          <CardContent>
            {!selectedCollection ? (
              <div className="text-center py-12 text-slate-500">
                <Database size={48} className="mx-auto mb-4 opacity-50" />
                <p>Pilih collection dari daftar di sebelah kiri</p>
              </div>
            ) : loading ? (
              <div className="text-center py-12 text-slate-500">Loading...</div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <p>Tidak ada data{searchQuery ? ` untuk pencarian "${searchQuery}"` : ''}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table data-testid="documents-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox 
                            checked={selectedDocs.length === documents.length && documents.length > 0}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                        {getDisplayColumns().map((col) => (
                          <TableHead key={col} className="text-xs uppercase">{col}</TableHead>
                        ))}
                        <TableHead className="w-24 text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents.map((doc) => (
                        <TableRow key={doc.id} className="hover:bg-slate-50">
                          <TableCell>
                            <Checkbox 
                              checked={selectedDocs.includes(doc.id)}
                              onCheckedChange={() => toggleSelectDoc(doc.id)}
                              data-testid={`checkbox-${doc.id}`}
                            />
                          </TableCell>
                          {getDisplayColumns().map((col) => (
                            <TableCell key={col} className="text-sm max-w-[200px] truncate">
                              {formatCellValue(doc[col])}
                            </TableCell>
                          ))}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openEditDialog(doc)}
                                data-testid={`edit-${doc.id}`}
                              >
                                <Edit size={14} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => openDeleteDialog(doc.id, false)}
                                data-testid={`delete-${doc.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-sm text-slate-500">
                      Halaman {currentPage} dari {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                        title="Halaman pertama"
                        data-testid="first-page-btn"
                      >
                        <ChevronsLeft size={16} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft size={16} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight size={16} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        title="Halaman terakhir"
                        data-testid="last-page-btn"
                      >
                        <ChevronsRight size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, document: null })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Dokumen</DialogTitle>
            <DialogDescription>
              Collection: {selectedCollection} | ID: {editDialog.document?.id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <textarea
              value={editData}
              onChange={(e) => setEditData(e.target.value)}
              className="w-full h-[300px] p-3 font-mono text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              spellCheck={false}
              data-testid="edit-textarea"
            />
          </div>

          <div className="space-y-3 pt-4 border-t">
            <div>
              <label className="text-sm font-medium text-slate-700">Konfirmasi Password</label>
              <Input
                type="password"
                placeholder="Masukkan password Anda"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                className="mt-1"
                data-testid="edit-password-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, document: null })}>
              Batal
            </Button>
            <Button onClick={handleSaveEdit} data-testid="save-edit-btn">
              <Save size={16} className="mr-2" />
              Simpan Perubahan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, documentId: null, isBulk: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Konfirmasi Hapus
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.isBulk 
                ? `Anda akan menghapus ${selectedDocs.length} dokumen dari collection "${selectedCollection}".`
                : `Anda akan menghapus dokumen dengan ID: ${deleteDialog.documentId}`
              }
              <br /><br />
              <strong>Tindakan ini tidak dapat dibatalkan!</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium text-slate-700">Konfirmasi Password</label>
            <Input
              type="password"
              placeholder="Masukkan password Anda"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="mt-1"
              data-testid="delete-password-input"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              <Trash2 size={16} className="mr-2" />
              Hapus {deleteDialog.isBulk ? `(${selectedDocs.length})` : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DatabaseEditor;
